import React, { FunctionComponent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppState } from 'src/shared/appState';
import { ZakekeEnvironment } from 'src/shared/enums';
import { useFeatureFlagWithConditions } from 'src/shared/feature-flags';
import { T, backofficeUrl } from 'src/shared/helpers';
import { EnvType } from 'src/shared/hooks';
import { useAccountData } from 'src/shared/hooks/useAccountData';
import { usePageTransition } from 'src/shared/navigationTransition';
import AngleDown from 'src/shared/svg/angleDown';
import AutoAwesomeIcon from 'src/shared/material_icons/image/auto_awesome.svg';
import GridViewIcon from 'src/shared/material_icons/file/grid_view.svg';
import theme from 'src/shared/theme';
import styled from 'styled-components';
import { useLastUsedTool } from '../../../shared/hooks';
import {
    DropdownContent,
    DropdownDivider,
    DropdownItemBody,
    DropdownItemCaption,
    DropdownItemIcon,
    DropdownItemText,
    DropdownList,
    DropdownListItem,
    useDropdown
} from '../dropdown';
import { Icon } from '../icon';
import { AiTag, EarlyAccessTag, NewTag } from '../tag';
import { ONBOARDING_FROM_APP_QUERY } from '../../pages/onboarding/onboarding';
import { getSolutionTools } from './solutionTools';
import { showErrorNotification } from '../notificationToaster';
import { useOnboardingEventTracking } from 'src/shared/hooks/useOnboardingEventTracking';

// Solution switcher moved from topbar. Sits at the top of the sidebar and
// lets the user jump between Visual Product Customizer, 3D Product
// Configurator, 3D DAM & AR Viewer, AI Agents Studio.

const Container = styled.div`
    position: relative;
    margin: 0 12px 10px;
`;

const Trigger = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 10px;
    background: #f9f9f9;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    cursor: pointer;
    font-family: inherit;
    color: #333;
    transition: background 0.15s ease;

    &:hover {
        background: rgba(0, 0, 0, 0.07);
    }
`;

const TriggerIcon = styled(DropdownItemIcon).attrs({ $size: 'large' as const })`
    flex-shrink: 0;
`;

const TriggerLabel = styled.span`
    flex: 1;
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const AiGradientLabel = styled(TriggerLabel)`
    background: ${theme.ai.gradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
`;

const AiGradientItemText = styled.span`
    background: ${theme.ai.gradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
`;

// The two onboarding entries are one row each, whatever the caption: a fixed height keeps them
// identical to one another and to the tools above.
const OnboardingListItem = styled(DropdownListItem)`
    min-height: 52px;
`;

const OnboardingCaption = styled(DropdownItemCaption)`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const TriggerArrow = styled.span<{ $isOpen: boolean }>`
    display: inline-flex;
    transform: rotate(${(p) => (p.$isOpen ? '180deg' : '0deg')});
    transition: transform 0.2s ease;

    svg {
        width: 10px;
        height: 10px;
    }
`;

const SolutionSwitcher: FunctionComponent = () => {
    const { state } = useAppState();
    const { setLastUsedTool } = useLastUsedTool();
    const { userInfo, ecommerceSettings } = useAccountData();
    const navigate = useNavigate();
    const { startPageTransition } = usePageTransition();
    const [openDropdown, closeDropdown, isOpen, Dropdown] = useDropdown();

    const userID = userInfo?.userID;
    const isNewConfiguratorBackoffice = useFeatureFlagWithConditions('newConfiguratorBackoffice', {
        userId: (userID || 0).toString()
    });

    const tools = getSolutionTools(isNewConfiguratorBackoffice);

    const getActiveToolType = (): string => {
        if (state.lastUsedTool === ZakekeEnvironment.AIStudio) return 'AI_STUDIO';
        if (isNewConfiguratorBackoffice) {
            switch (state.lastUsedTool) {
                case ZakekeEnvironment.Customizer:
                    return 'CUSTOMIZER';
                case ZakekeEnvironment.Composer:
                    return 'CONFIGURATOR';
                case ZakekeEnvironment.Viewer:
                    return 'VIEWER';
                default:
                    return 'CUSTOMIZER';
            }
        }
        const url = window.location.href;
        if (url.indexOf('landings/configurator') !== -1 || url.indexOf('/Composer') !== -1) return 'CONFIGURATOR';
        if (url.indexOf('landings/viewer') !== -1 || url.toLowerCase().indexOf('Admin/DAM'.toLowerCase()) !== -1) return 'VIEWER';
        return 'CUSTOMIZER';
    };

    const activeToolType = getActiveToolType();
    const activeTool = tools.find((t) => t.type === activeToolType);
    const trackEvent = useOnboardingEventTracking();
    const isWix = ecommerceSettings?.ecommerceName?.toUpperCase() === 'WIX';

    if (!userID) return null;

    const handleSelect = async (tool: (typeof tools)[number]) => {
        closeDropdown();
        trackEvent({
            eventName: 'solution_switched',
            entryPoint: 'navigation',
            selectionMode: 'navigation',
            currentSolution: activeToolType,
            targetSolution: tool.type,
            context: { source: 'solution_switcher' }
        });
        try {
            await setLastUsedTool(tool.type as EnvType);
        } catch {
            showErrorNotification({ message: T._('Unable to switch solution. Please try again.', 'Admin') });
            return;
        }
        if (tool.type === 'AI_STUDIO') {
            startPageTransition(() => navigate(tool.url));
        } else {
            window.location.href = tool.url;
        }
    };

    // The onboarding screens stay reachable from inside a tool: the descriptive page of every solution,
    // and the same page opened on the box that suggests one from a description. The flag tells the page
    // it was opened from the app rather than right after signup, so it can offer a way back.
    const goToSolutions = (describe = false) => {
        closeDropdown();
        startPageTransition(() => {
            navigate(`${backofficeUrl(describe ? '/onboarding/ask-zakeke' : '/onboarding/solutions')}?${ONBOARDING_FROM_APP_QUERY}`);
        });
    };

    const isAiActive = activeToolType === 'AI_STUDIO';

    return (
        <Container>
            <Trigger
                id='tool-switcher'
                onClick={(e) => (isOpen ? closeDropdown() : openDropdown(e.currentTarget, 'bottom-start'))}
                data-testid='sidebar-solution-switcher'
                aria-haspopup='menu'
                aria-expanded={isOpen}
            >
                <TriggerIcon>
                    <Icon>{activeTool?.icon}</Icon>
                </TriggerIcon>
                {isAiActive ? <AiGradientLabel>{activeTool?.name}</AiGradientLabel> : <TriggerLabel>{activeTool?.name}</TriggerLabel>}
                <TriggerArrow $isOpen={isOpen}>
                    <AngleDown />
                </TriggerArrow>
            </Trigger>
            {isOpen && (
                <Dropdown width='360px' surfacePadding='0'>
                    <DropdownContent>
                        <DropdownList>
                            {tools.map((tool) => {
                                if (isWix && tool.type === 'CONFIGURATOR') return null;
                                return (
                                    <DropdownListItem key={tool.type} $active={tool.type === activeToolType} role='menuitem' onClick={() => handleSelect(tool)}>
                                        <DropdownItemIcon $size='large'>
                                            <Icon>{tool.icon}</Icon>
                                        </DropdownItemIcon>
                                        <DropdownItemBody>
                                            <DropdownItemText>
                                                {tool.name}
                                                {tool.isAI && <AiTag>AI</AiTag>}
                                                {tool.isNew && <NewTag>New</NewTag>}
                                                {tool.isEarlyAccess && <EarlyAccessTag>Early Access</EarlyAccessTag>}
                                            </DropdownItemText>
                                            <DropdownItemCaption>{tool.description}</DropdownItemCaption>
                                        </DropdownItemBody>
                                    </DropdownListItem>
                                );
                            })}

                            <DropdownDivider />

                            <OnboardingListItem role='menuitem' onClick={() => goToSolutions()}>
                                <DropdownItemIcon $size='large'>
                                    <Icon>
                                        <GridViewIcon />
                                    </Icon>
                                </DropdownItemIcon>
                                <DropdownItemBody>
                                    <DropdownItemText>{T._('See all solutions', 'Admin')}</DropdownItemText>
                                    <OnboardingCaption>{T._('What each solution does, side by side.', 'Admin')}</OnboardingCaption>
                                </DropdownItemBody>
                            </OnboardingListItem>

                            <OnboardingListItem role='menuitem' onClick={() => goToSolutions(true)}>
                                <DropdownItemIcon $size='large'>
                                    <Icon>
                                        <AutoAwesomeIcon />
                                    </Icon>
                                </DropdownItemIcon>
                                <DropdownItemBody>
                                    <DropdownItemText>
                                        <AiGradientItemText>{T._('Ask Zakeke which one fits', 'Admin')}</AiGradientItemText>
                                    </DropdownItemText>
                                    <OnboardingCaption>{T._('Get a suggestion from a description.', 'Admin')}</OnboardingCaption>
                                </DropdownItemBody>
                            </OnboardingListItem>
                        </DropdownList>
                    </DropdownContent>
                </Dropdown>
            )}
        </Container>
    );
};

export default SolutionSwitcher;
